"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

export type MediaSlide = {
  /** Video (.mp4, with `poster`) or photo. */
  src: string;
  poster?: string;
  alt: string;
  caption: string;
  /** Crop in on photos where the phone is small in the frame. */
  zoom?: boolean;
};

/** Videos play faster than recorded so each slide gets to the point. */
const PLAYBACK_RATE = 1.5;
const PHOTO_DURATION = 3500;

const isVideo = (slide: MediaSlide) => slide.src.endsWith(".mp4");

/**
 * Crossfading slideshow of videos and photos. Each video plays once, sped up,
 * then the next slide fades in; photos stay up for a few seconds. For anyone
 * with "reduce motion" turned on, nothing moves by itself: they step through
 * with the arrows and see each video's first frame.
 */
export function MediaCarousel({
  slides,
  label,
  tone = "dark",
  priority,
  className,
  frameClassName = "aspect-[3/4]",
}: {
  slides: readonly MediaSlide[];
  label: string;
  /** "dark" on the black bands, "light" on white sections. */
  tone?: "dark" | "light";
  /** Load the first photo straight away (use above the fold). */
  priority?: boolean;
  className?: string;
  frameClassName?: string;
}) {
  const [index, setIndex] = useState(0);
  const videos = useRef<(HTMLVideoElement | null)[]>([]);
  const reduceMotion = useReducedMotion();
  const go = (i: number) => setIndex((i + slides.length) % slides.length);
  const dark = tone === "dark";

  // Play the current video from the start and stop the others; photos advance on a timer.
  useEffect(() => {
    videos.current.forEach((video, i) => {
      if (!video) return;
      if (i === index && !reduceMotion) {
        video.currentTime = 0;
        video.playbackRate = PLAYBACK_RATE;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
    if (reduceMotion || isVideo(slides[index]) || slides.length < 2) return;
    const id = setTimeout(() => setIndex((i) => (i + 1) % slides.length), PHOTO_DURATION);
    return () => clearTimeout(id);
  }, [index, reduceMotion, slides]);

  return (
    <section aria-roledescription="carousel" aria-label={label} className={cn("relative", className)}>
      <div className={cn("relative overflow-hidden rounded-2xl", dark ? "bg-ink-foreground/10" : "bg-surface", frameClassName)}>
        {slides.map((slide, i) => (
          <figure
            key={slide.src}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            aria-hidden={i !== index}
            className={cn(
              "absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none",
              i === index ? "opacity-100" : "opacity-0",
            )}
          >
            {isVideo(slide) ? (
              <video
                ref={(el) => {
                  videos.current[i] = el;
                }}
                src={slide.src}
                poster={slide.poster}
                muted
                playsInline
                preload={i === index ? "auto" : "metadata"}
                aria-label={slide.alt}
                onEnded={() => go(i + 1)}
                className="size-full object-cover"
              />
            ) : (
              <Image
                src={slide.src}
                alt={slide.alt}
                fill
                priority={priority && i === 0}
                sizes="(min-width: 768px) 40vw, 100vw"
                className={cn("object-cover", slide.zoom && "scale-150")}
              />
            )}
            <figcaption className="absolute bottom-4 left-4 right-4 w-fit rounded-full bg-ink/80 px-4 py-1.5 text-sm font-medium text-ink-foreground backdrop-blur">
              {slide.caption}
            </figcaption>
          </figure>
        ))}
      </div>

      {slides.length > 1 && (
        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="flex gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                onClick={() => go(i)}
                aria-label={`Show slide ${i + 1}: ${slide.caption}`}
                aria-current={i === index}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  i === index
                    ? cn("w-6", dark ? "bg-brand-lime" : "bg-brand-green")
                    : cn("w-2", dark ? "bg-ink-foreground/35 hover:bg-ink-foreground/60" : "bg-input hover:bg-muted-foreground"),
                )}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <ArrowButton dark={dark} label="Previous slide" onClick={() => go(index - 1)}>
              <ChevronLeft />
            </ArrowButton>
            <ArrowButton dark={dark} label="Next slide" onClick={() => go(index + 1)}>
              <ChevronRight />
            </ArrowButton>
          </div>
        </div>
      )}
    </section>
  );
}

function ArrowButton({
  dark,
  label,
  onClick,
  children,
}: {
  dark: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex size-10 items-center justify-center rounded-full border transition-colors [&_svg]:size-5",
        dark
          ? "border-ink-foreground/25 text-ink-foreground hover:bg-ink-foreground/10"
          : "border-input text-foreground hover:bg-surface",
      )}
    >
      {children}
    </button>
  );
}
